// ShipListDropdownMenu.tsx
import React, { useState } from 'react';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
} from '@ais-ui/ui-core';
import { PiDotsThreeVertical } from 'react-icons/pi';
import { DetailedShipList, ShipList } from '@ais-ui/api-spec/config';
import { useGetShiplist } from '@ais-ui/api';
import { useLocalization } from '@ais-ui/locale-management';
import Papa from 'papaparse';

interface ShiplistDropdownMenuDetailItemsProps {
  shiplist: ShipList;
  onShowShips: (detailedShipList: DetailedShipList) => void;
  onDownload: (detailedShiplist: DetailedShipList) => void;
  isDownloading: boolean;
}

const ShiplistDropdownMenuDetailItems = ({
  shiplist,
  onShowShips,
  onDownload,
  isDownloading,
}: ShiplistDropdownMenuDetailItemsProps) => {
  const { data: detailedShiplist, isLoading } = useGetShiplist(
    shiplist.id as string
  );

  return (
    <>
      <DropdownMenuItem
        onClick={() => onShowShips(detailedShiplist as DetailedShipList)}
        disabled={isLoading}
      >
        Ships
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={() => onDownload(detailedShiplist as DetailedShipList)}
        disabled={isLoading || isDownloading}
      >
        {isDownloading && (
          <div className="flex items-center">
            <Icons.spinner className="h-4 w-4 animate-spin mr-2" />
            Downloading
          </div>
        )}
        {!isDownloading && <div>Download</div>}
      </DropdownMenuItem>
    </>
  );
};

interface ShipListDropdownMenuProps {
  shiplist: ShipList;
  onShowShips: (detailedShiplist: DetailedShipList) => void;
  onDelete: () => void;
  onEdit: () => void;
  className?: string;
}

export const ShipListDropdownMenu = ({
  shiplist,
  onShowShips,
  onDelete,
  onEdit,
  className,
}: ShipListDropdownMenuProps) => {
  const { messages } = useLocalization();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const handleDownload = (detailedShiplist: DetailedShipList) => {
    if (!detailedShiplist || !detailedShiplist.ships) {
      return;
    }

    setIsDownloading(true);

    const csv = Papa.unparse(detailedShiplist.ships, {
      header: true,
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `shiplist_${shiplist.name}.csv`;
    a.click();

    URL.revokeObjectURL(url);
    setIsDownloading(false);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button
          id={`${shiplist.name}-dropdown-button`}
          size="icon"
          variant="ghost"
          className="h-8 w-8"
        >
          <PiDotsThreeVertical className="h-6 w-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <ShiplistDropdownMenuDetailItems
          shiplist={shiplist}
          onShowShips={onShowShips}
          onDownload={handleDownload}
          isDownloading={isDownloading}
        />
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onEdit}>
          {messages.genericEdit}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete}>
          {messages.genericDelete}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
